import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { PageTitle } from '../../../../../_metronic/layout/core'
import useAuth from '../../../../hooks/useAuth'
import { getSessionHistory } from '../../SellerQueue/services'
import { SellerServiceSessionItem } from '../../SellerQueue/types'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { createDefaultSellerReportDateRange, useSellerReportFilters } from '../useSellerReportFilters'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface ConversionSellerRow {
  sellerId: number
  sellerLabel: string
  totalSessions: number
  completedSales: number
  potentialSales: number
  noSales: number
  abandoned: number
  strictConversionRate: number
  potentialConversionRate: number
}

interface ConversionSummary {
  totalSessions: number
  completedSales: number
  potentialSales: number
  noSales: number
  abandoned: number
  strictConversionRate: number
  potentialConversionRate: number
}

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const numberFormatter = new Intl.NumberFormat('pt-BR')

const getSellerLabel = (session: SellerServiceSessionItem): string => {
  const fallbackName = `Vendedor ${session.sellerId}`
  const rawName = session.sellerName?.trim()

  return rawName && rawName.length > 0 ? rawName : fallbackName
}

const toPercent = (value: number, total: number): number => {
  if (!total) {
    return 0
  }

  return (value / total) * 100
}

const exportToCSV = (filename: string, data: ConversionSellerRow[]): void => {
  let csv = 'sep=;\n'
  csv += 'Posição;Vendedor;Atendimentos;Vendas concluídas;Checkout pendente;Sem venda;Abandono;Conversão conservadora (%);Conversão potencial (%)\n'

  data.forEach((row, index) => {
    const pendingCheckout = row.potentialSales - row.completedSales
    csv += `${index + 1};"${row.sellerLabel}";${row.totalSessions};${row.completedSales};${pendingCheckout};${row.noSales};${row.abandoned};${row.strictConversionRate.toFixed(1)};${row.potentialConversionRate.toFixed(1)}\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, data: ConversionSellerRow[]): void => {
  const rows = data.map((row, index) => {
    const pendingCheckout = row.potentialSales - row.completedSales

    return {
      Posição: index + 1,
      Vendedor: row.sellerLabel,
      Atendimentos: row.totalSessions,
      'Vendas concluídas': row.completedSales,
      'Checkout pendente': pendingCheckout,
      'Sem venda': row.noSales,
      Abandono: row.abandoned,
      'Conversão conservadora (%)': row.strictConversionRate.toFixed(1),
      'Conversão potencial (%)': row.potentialConversionRate.toFixed(1),
    }
  })

  const worksheet = XLSX.utils.json_to_sheet(rows)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Conversao')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (filename: string, data: ConversionSellerRow[], summary: ConversionSummary): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Conversão por Vendedor', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Indicadores Consolidados', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  const lines = [
    `Atendimentos: ${summary.totalSessions} | Vendas concluídas: ${summary.completedSales} | Checkout pendente: ${summary.potentialSales - summary.completedSales}`,
    `Conversão conservadora: ${summary.strictConversionRate.toFixed(1)}% | Conversão potencial: ${summary.potentialConversionRate.toFixed(1)}%`,
    `Sem venda: ${summary.noSales} | Abandono: ${summary.abandoned}`,
  ]

  lines.forEach((line) => {
    pdf.text(line, margin, yPosition)
    yPosition += 5
  })

  yPosition += 5

  autoTable(pdf, {
    startY: yPosition,
    head: [[
      'Pos',
      'Vendedor',
      'Atend.',
      'Vendas',
      'Pend.',
      'Sem venda',
      'Abandono',
      'Conv. Cons.',
      'Conv. Pot.',
    ]],
    body: data.map((row, index) => [
      index + 1,
      row.sellerLabel,
      row.totalSessions,
      row.completedSales,
      row.potentialSales - row.completedSales,
      row.noSales,
      row.abandoned,
      row.strictConversionRate.toFixed(1),
      row.potentialConversionRate.toFixed(1),
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: {
      1: { halign: 'left' },
      2: { halign: 'center' },
      3: { halign: 'center' },
      4: { halign: 'center' },
      5: { halign: 'center' },
      6: { halign: 'center' },
      7: { halign: 'center' },
      8: { halign: 'center' },
    },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const SellerConversionRateReportPage: FC = () => {
  const { loginInfo } = useAuth()
  const pluginspaceId = Number(loginInfo.pluginspaceId || 0)

  const { filters, setFilters, isReady } = useSellerReportFilters({
    ...createDefaultSellerReportDateRange(),
  })
  const [loading, setLoading] = useState(false)
  const [sessions, setSessions] = useState<SellerServiceSessionItem[]>([])

  const loadReport = useCallback(async () => {
    if (!pluginspaceId || pluginspaceId <= 0) {
      setSessions([])
      return
    }

    try {
      setLoading(true)
      const data = await getSessionHistory(pluginspaceId, filters.startDate, filters.finalDate)
      setSessions(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters.finalDate, filters.startDate, pluginspaceId])

  useEffect(() => {
    if (isReady) {
      void loadReport()
    }
  }, [isReady, loadReport])

  const summary = useMemo<ConversionSummary>(() => {
    const totalSessions = sessions.length
    const completedSales = sessions.filter((session) => session.outcome === 'sale_completed').length
    const potentialSales = sessions.filter(
      (session) => session.outcome === 'sale_completed' || session.outcome === 'pending_checkout'
    ).length
    const noSales = sessions.filter((session) => session.outcome === 'no_sale').length
    const abandoned = sessions.filter((session) => session.outcome === 'abandoned').length

    return {
      totalSessions,
      completedSales,
      potentialSales,
      noSales,
      abandoned,
      strictConversionRate: toPercent(completedSales, totalSessions),
      potentialConversionRate: toPercent(potentialSales, totalSessions),
    }
  }, [sessions])

  const sellerRows = useMemo<ConversionSellerRow[]>(() => {
    const grouped = new Map<number, ConversionSellerRow>()

    sessions.forEach((session) => {
      const existing = grouped.get(session.sellerId)
      if (existing) {
        existing.totalSessions += 1

        if (session.outcome === 'sale_completed') {
          existing.completedSales += 1
          existing.potentialSales += 1
        } else if (session.outcome === 'pending_checkout') {
          existing.potentialSales += 1
        } else if (session.outcome === 'no_sale') {
          existing.noSales += 1
        } else if (session.outcome === 'abandoned') {
          existing.abandoned += 1
        }

        return
      }

      grouped.set(session.sellerId, {
        sellerId: session.sellerId,
        sellerLabel: getSellerLabel(session),
        totalSessions: 1,
        completedSales: session.outcome === 'sale_completed' ? 1 : 0,
        potentialSales:
          session.outcome === 'sale_completed' || session.outcome === 'pending_checkout' ? 1 : 0,
        noSales: session.outcome === 'no_sale' ? 1 : 0,
        abandoned: session.outcome === 'abandoned' ? 1 : 0,
        strictConversionRate: 0,
        potentialConversionRate: 0,
      })
    })

    const rows = Array.from(grouped.values()).map((row) => ({
      ...row,
      strictConversionRate: toPercent(row.completedSales, row.totalSessions),
      potentialConversionRate: toPercent(row.potentialSales, row.totalSessions),
    }))

    rows.sort((a, b) => {
      if (b.strictConversionRate !== a.strictConversionRate) {
        return b.strictConversionRate - a.strictConversionRate
      }

      return b.totalSessions - a.totalSessions
    })

    return rows
  }, [sessions])

  return (
    <>
      <PageTitle
        breadcrumbs={[
          { title: 'Taxa de Conversão por Vendedor', path: '/seller/reports/conversion-rate', isActive: true },
        ]}
      >
        Relatório de Conversão por Atendimento
      </PageTitle>

      <ReportShell
        title='Taxa de Conversão por Vendedor'
        description='Conversão baseada em sessões de atendimento da fila de vendedor, separando visão conservadora e potencial.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button
              className='btn btn-light-success'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToCSV(`Conversao-Vendedor-${timestamp}.csv`, sellerRows)
              }}
              disabled={loading || sellerRows.length === 0}
              title='Baixar relatório em CSV'
            >
              <i className='fas fa-file-csv me-2'></i>
              CSV
            </button>
            <button
              className='btn btn-light-info'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToXLSX(`Conversao-Vendedor-${timestamp}.xlsx`, sellerRows)
              }}
              disabled={loading || sellerRows.length === 0}
              title='Baixar relatório em XLSX'
            >
              <i className='fas fa-file-excel me-2'></i>
              XLSX
            </button>
            <button
              className='btn btn-light-danger'
              onClick={() => {
                const timestamp = new Date().toISOString().split('T')[0]
                exportToPDF(`Conversao-Vendedor-${timestamp}.pdf`, sellerRows, summary)
              }}
              disabled={loading || sellerRows.length === 0}
              title='Baixar relatório em PDF'
            >
              <i className='fas fa-file-pdf me-2'></i>
              PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        {!pluginspaceId ? (
          <div className='alert alert-warning mb-0'>
            Pluginspace nao identificado para o usuario logado.
          </div>
        ) : (
          <>
            <div className='row g-4 mb-4'>
              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Total de atendimentos</div>
                    <div className='fs-2hx fw-bold text-primary'>
                      {numberFormatter.format(summary.totalSessions)}
                    </div>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Conversão conservadora</div>
                    <div className='fs-2hx fw-bold text-success'>
                      {percentFormatter.format(summary.strictConversionRate)}%
                    </div>
                    <div className='text-gray-700 mt-2'>
                      {numberFormatter.format(summary.completedSales)} vendas concluídas
                    </div>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Conversão potencial</div>
                    <div className='fs-2hx fw-bold text-info'>
                      {percentFormatter.format(summary.potentialConversionRate)}%
                    </div>
                    <div className='text-gray-700 mt-2'>
                      {numberFormatter.format(summary.potentialSales)} vendas + checkout pendente
                    </div>
                  </div>
                </div>
              </div>

              <div className='col-12 col-md-6 col-xl-3'>
                <div className='card border-0 shadow-sm h-100'>
                  <div className='card-body'>
                    <div className='text-gray-600 fw-semibold mb-1'>Sem venda / abandono</div>
                    <div className='fs-2hx fw-bold text-dark'>
                      {numberFormatter.format(summary.noSales + summary.abandoned)}
                    </div>
                    <div className='text-gray-700 mt-2'>
                      {numberFormatter.format(summary.noSales)} sem venda e {numberFormatter.format(summary.abandoned)} abandono
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className='card border-0 shadow-sm'>
              <div className='card-header border-0 pt-5'>
                <div className='card-title d-flex flex-column'>
                  <span className='fs-3 fw-bold text-dark'>Conversão por vendedor</span>
                  <span className='text-gray-600 mt-1'>
                  Conservador = apenas vendas concluídas. Potencial = vendas concluídas + checkout pendente.
                  </span>
                </div>
              </div>
              <div className='card-body pt-0'>
                {loading ? (
                  <div className='d-flex justify-content-center align-items-center py-10'>
                    <div className='spinner-border text-primary' role='status'>
                      <span className='visually-hidden'>Carregando...</span>
                    </div>
                  </div>
                ) : sellerRows.length > 0 ? (
                  <div className='table-responsive'>
                    <table className='table align-middle table-row-dashed fs-6 gy-3'>
                      <thead>
                        <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                          <th>Posição</th>
                          <th>Vendedor</th>
                          <th className='text-end'>Atendimentos</th>
                          <th className='text-end'>Vendas concluídas</th>
                          <th className='text-end'>Checkout pendente</th>
                          <th className='text-end'>Sem venda</th>
                          <th className='text-end'>Abandono</th>
                          <th className='text-end'>Conversão conservadora</th>
                          <th className='text-end'>Conversão potencial</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sellerRows.map((row, index) => {
                          const pendingCheckout = row.potentialSales - row.completedSales

                          return (
                            <tr key={row.sellerId}>
                              <td>#{index + 1}</td>
                              <td className='fw-bold text-dark'>
                                <SellerIdentity label={row.sellerLabel} />
                              </td>
                              <td className='text-end'>{numberFormatter.format(row.totalSessions)}</td>
                              <td className='text-end'>{numberFormatter.format(row.completedSales)}</td>
                              <td className='text-end'>{numberFormatter.format(pendingCheckout)}</td>
                              <td className='text-end'>{numberFormatter.format(row.noSales)}</td>
                              <td className='text-end'>{numberFormatter.format(row.abandoned)}</td>
                              <td className='text-end fw-semibold text-success'>
                                {percentFormatter.format(row.strictConversionRate)}%
                              </td>
                              <td className='text-end fw-semibold text-info'>
                                {percentFormatter.format(row.potentialConversionRate)}%
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className='alert alert-light-info mb-0'>
                    Nenhuma sessão encontrada para os filtros selecionados.
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </ReportShell>
    </>
  )
}

export { SellerConversionRateReportPage }

